import React, { useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { useTranslation } from 'react-i18next';
import { formatDisplayDateTime } from '../../../utils/dateTimeFormat';

// Mapping of keys to their meaningful display names
const displayNamesMap: { [key: string]: string } = {
  ConfigChange: "Configuration Change",
  ModuleRestart: "Module Restart",
  CpuUtilization: "CPU Utilization",
  MemoryUtilization: "Memory Utilization",
  DiskUtilization: "Disk Utilization",
  LoginFail: "Login Fail",
  ModuleFault: "Module Fault",
  EmailServerConnectFail: "Email Server Connect Fail",
  FtpServer1ConnectFail: "FTP Server 1 Connect Fail",
  FtpServer2ConnectFail: "FTP Server 2 Connect Fail",
  FtpServer3ConnectFail: "FTP Server 3 Connect Fail",
  HttpServer3ConnectFail: "HTTP Server 3 Connect Fail",
  PasswordChange: "Password Change",
  IOConnFail: "I/O Connection Fail",
  MbSlave1ConnFail: "Modbus Slave 1 Connection Fail",
  MbSlave2ConnFail: "Modbus Slave 2 Connection Fail",
  MbSlave3ConnFail: "Modbus Slave 3 Connection Fail",
  MbSlave4ConnFail: "Modbus Slave 4 Connection Fail",
  MbSlave5ConnFail: "Modbus Slave 5 Connection Fail",
  MbSlave6ConnFail: "Modbus Slave 6 Connection Fail",
  MbSlave7ConnFail: "Modbus Slave 7 Connection Fail",
  MbSlave8ConnFail: "Modbus Slave 8 Connection Fail",
  MbSlave9ConnFail: "Modbus Slave 9 Connection Fail",
  MbSlave10ConnFail: "Modbus Slave 10 Connection Fail",
  MbSlave11ConnFail: "Modbus Slave 11 Connection Fail",
  MbSlave12ConnFail: "Modbus Slave 12 Connection Fail",
  MbSlave13ConnFail: "Modbus Slave 13 Connection Fail",
  MbSlave14ConnFail: "Modbus Slave 14 Connection Fail",
  MbSlave15ConnFail: "Modbus Slave 15 Connection Fail",
  MbSlave16ConnFail: "Modbus Slave 16 Connection Fail",
  MbSlave17ConnFail: "Modbus Slave 17 Connection Fail",
  MbSlave18ConnFail: "Modbus Slave 18 Connection Fail",
  MbSlave19ConnFail: "Modbus Slave 19 Connection Fail",
  MbSlave20ConnFail: "Modbus Slave 20 Connection Fail",
  ModemConnFail: "Modem Connection Fail",
  POCCommFail: "POC Communication Fail",
  SDCardDetectFail: "SD Card Detect Fail",
  TimeSyncFail: "Time Synchronization Fail",
  MotionDetect: "Motion Detect",
  SystemRestart: "System Restart",
  MqttServer1ConnectFail: "MQTT Server 1 Connect Fail",
  MqttServer2ConnectFail: "MQTT Server 2 Connect Fail",
  MqttServer3ConnectFail: "MQTT Server 3 Connect Fail",
  FtpFileTXCntLimit: "FTP File TX Count Limit",
  MqttFileTXCntLimit: "MQTT File TX Count Limit",
  WHartGatewayConnectFail: "W-Hart Gateway Connect Fail",
  SecondaryRTUConnectFail: "Secondary RTU Connect Fail",
  FtpFilesPending: "FTP Files Pending",
  MqttFilesPending: "MQTT Files Pending",
  BackupFtpServer1Fail: "Backup FTP Server 1 Fail",
  // Add more mappings as needed
};

const SystemEvents = ({ systemEvents, title }: { systemEvents: any; title?: string }) => {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    dateTimeString: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const statusOptions = [
    { label: t('rtuDetail.systemEventsTable.filters.alarm'), value: 'Alarm' },
    { label: t('rtuDetail.systemEventsTable.filters.noAlarm'), value: 'No Alarm' },
  ];

  // Transform systemEvents object into an array format
  const eventsArray = Object.entries(systemEvents ?? {}).map(([key, value]) => {
    const dateTimeString = formatDisplayDateTime((value as any).time);
    const displayName =
      displayNamesMap[(value as any).name] || (value as any).name;
    return {
      id: key,
      ...(value as any),
      name: displayName,
      dateTimeString,
    };
  });

  const systemEventStatusBodyTemplate = (rowData: any) => {
    const value = rowData.status as string | undefined;
    let color = '#64748b';
    let icon: 'pi-circle-on' | 'pi-circle-fill' = 'pi-circle-fill';
    let tooltip: string;

    if (value === 'Alarm') {
      color = 'red';
      icon = 'pi-circle-fill';
      tooltip = t('rtuDetail.systemEventsTable.statusIcon.alarm');
    } else if (value === 'No Alarm') {
      color = 'green';
      icon = 'pi-circle-on';
      tooltip = t('rtuDetail.systemEventsTable.statusIcon.noAlarm');
    } else {
      tooltip = t('rtuDetail.systemEventsTable.statusIcon.other', { value: String(value ?? '—') });
    }

    return (
      <span role="img" aria-label={tooltip} title={tooltip} className="rtu-status-icon-cell">
        <i className={`pi ${icon}`} style={{ color, fontSize: '0.7rem' }} aria-hidden />
      </span>
    );
  };

  const statusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="label"
        optionValue="value"
        placeholder=""
        className="p-column-filter"
        showClear
        style={{ width: '100%', minWidth: '5rem' }}
      />
    );
  };

  const dateTimeBodyTemplate = (rowData: any) => rowData.dateTimeString;

  const cardTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{title || t('rtuDetail.sections.systemEvents')}</span>
      <Button
        icon="pi pi-search"
        className="p-button-text p-button-sm"
        onClick={() => setShowFilters(!showFilters)}
        tooltip={showFilters ? t('search.hide') : t('search.show')}
        tooltipOptions={{ position: 'left' }}
        style={{ padding: '0.15rem 0.3rem', width: 'auto', height: 'auto', color: '#333' }}
      />
    </div>
  );

  return (
    <Card title={cardTitle} className="info-card compact-card flat-card">
      {!systemEvents || eventsArray.length === 0 ? (
        <div className="rtu-detail-empty-state">
          {t('rtuDetail.missing.notReported', { defaultValue: 'Not reported by this RTU.' })}
        </div>
      ) : (
      <div className="rtu-system-events-table-host">
        <DataTable
          value={eventsArray}
          showGridlines
          size="small"
          style={{ fontSize: '0.8rem' }}
          tableStyle={{ tableLayout: 'fixed', width: '100%', minWidth: '28rem' }}
          filters={filters}
          filterDisplay={showFilters ? "row" : undefined}
          onFilter={(e) => setFilters(e.filters as any)}
          paginator
          rows={8}
          paginatorTemplate="PrevPageLink PageLinks NextPageLink"
          paginatorClassName="compact-paginator"
        >
          <Column
            field="name"
            header={t('rtuDetail.systemEventsTable.columns.event')}
            style={{ minWidth: '11rem' }}
            bodyStyle={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            filter={showFilters}
            filterPlaceholder=""
            showFilterMenu={false}
          />
          <Column
            body={systemEventStatusBodyTemplate}
            field="status"
            header={t('rtuDetail.systemEventsTable.columns.status')}
            bodyStyle={{ textAlign: "center" }}
            headerStyle={{ textAlign: "center" }}
            style={{ whiteSpace: 'nowrap', width: '5.5rem' }}
            filter={showFilters}
            filterElement={statusRowFilterTemplate}
            showFilterMenu={false}
          />
          <Column
            field="dateTimeString"
            header={t('rtuDetail.systemEventsTable.columns.recorded')}
            body={dateTimeBodyTemplate}
            style={{ whiteSpace: 'nowrap', width: '11rem' }}
            bodyStyle={{ textAlign: "left" }}
            filter={showFilters}
            filterPlaceholder=""
            showFilterMenu={false}
          />
        </DataTable>
      </div>
      )}
    </Card>
  );
};

export default SystemEvents;
